<template>
  <SidebarComponent />
  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Backfill ordini TOP</h1>
          <p>Aggiorna topSkuHit / topSkuOrderAt su tutti gli ordini.</p>
        </div>

        <div class="actions">
          <button class="create-user-button staff-button-positive" :disabled="running" @click="startBackfill">
            <span class="material-icons">play_arrow</span>
            Avvia backfill
          </button>
          <button class="create-user-button stop staff-button-cancel" :disabled="!running" @click="stopBackfill">
            <span class="material-icons">stop</span>
            Ferma
          </button>
        </div>

        <div class="stats">
          <div><strong>Stato:</strong> {{ running ? 'In corso' : 'In attesa' }}</div>
          <div><strong>Ordini letti:</strong> {{ processed }}</div>
          <div><strong>Ordini aggiornati:</strong> {{ updated }}</div>
          <div><strong>Batch commit:</strong> {{ commits }}</div>
          <div v-if="lastDocId"><strong>Ultimo doc:</strong> {{ lastDocId }}</div>
        </div>

        <div class="log-box">
          <pre>{{ logs }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { getFirestore, collection, getDocs, query, orderBy, startAfter, limit, writeBatch } from 'firebase/firestore';
import SidebarComponent from '../SidebarComponent.vue';
import { buildTopSkuFields } from '@/lib/topSku';

const PAGE_SIZE = 400;
const BATCH_SIZE = 300;

const toMs = (value) => {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() : null;
  }
  const parsed = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

export default {
  components: {
    SidebarComponent,
  },
  setup() {
    const db = getFirestore();
    const running = ref(false);
    const stopRequested = ref(false);
    const processed = ref(0);
    const updated = ref(0);
    const commits = ref(0);
    const logs = ref('');
    const lastDocId = ref('');

    const appendLog = (line) => {
      logs.value += `${line}\n`;
    };

    const startBackfill = async () => {
      if (running.value) return;
      running.value = true;
      stopRequested.value = false;
      processed.value = 0;
      updated.value = 0;
      commits.value = 0;
      logs.value = '';
      lastDocId.value = '';

      appendLog('▶️ Avvio backfill...');

      try {
        const ordersRef = collection(db, 'orders');
        let lastDoc = null;

        while (!stopRequested.value) {
          const q = lastDoc
            ? query(ordersRef, orderBy('__name__'), startAfter(lastDoc), limit(PAGE_SIZE))
            : query(ordersRef, orderBy('__name__'), limit(PAGE_SIZE));
          const snap = await getDocs(q);
          if (snap.empty) break;

          let batch = writeBatch(db);
          let batchCount = 0;

          for (const docSnap of snap.docs) {
            const data = docSnap.data() || {};
            const nextFields = buildTopSkuFields(data, { fallbackToNow: false });

            const currentHit = Boolean(data.topSkuHit);
            const currentAt = toMs(data.topSkuOrderAt) || 0;

            const nextHit = Boolean(nextFields.topSkuHit);
            const nextAt = Number(nextFields.topSkuOrderAt) || 0;

            const needsUpdate = currentHit !== nextHit || currentAt !== nextAt;
            if (needsUpdate) {
              batch.update(docSnap.ref, {
                topSkuHit: nextHit,
                topSkuOrderAt: nextAt,
              });
              batchCount += 1;
              updated.value += 1;
            }

            processed.value += 1;
            lastDocId.value = docSnap.id;

            if (batchCount >= BATCH_SIZE) {
              await batch.commit();
              commits.value += 1;
              batch = writeBatch(db);
              batchCount = 0;
            }
          }

          if (batchCount > 0) {
            await batch.commit();
            commits.value += 1;
          }

          lastDoc = snap.docs[snap.docs.length - 1];
        }

        appendLog(stopRequested.value ? '⏹️ Backfill fermato.' : '✅ Backfill completato.');
      } catch (err) {
        console.error('Backfill TOP SKU error:', err);
        appendLog(`❌ Errore: ${err?.message || err}`);
      } finally {
        running.value = false;
      }
    };

    const stopBackfill = () => {
      if (!running.value) return;
      stopRequested.value = true;
      appendLog('⏹️ Stop richiesto, attendi il commit del batch...');
    };

    return {
      running,
      processed,
      updated,
      commits,
      logs,
      lastDocId,
      startBackfill,
      stopBackfill,
    };
  },
};
</script>

<style scoped>
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.create-user-button.stop {
  background: linear-gradient(120deg, #ef4444, #b91c1c);
  box-shadow: 0 12px 28px rgba(239, 68, 68, 0.3);
}

.stats {
  display: grid;
  gap: 8px;
  padding: 16px 18px;
  border-radius: 16px;
  border: 1px solid #dbe2f0;
  background: #f8fafc;
  margin-bottom: 16px;
  font-size: 0.95rem;
}

.log-box {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #0f172a;
  color: #e2e8f0;
  padding: 14px 16px;
  min-height: 160px;
  max-height: 360px;
  overflow: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.85rem;
  white-space: pre-wrap;
}
</style>
